import { Typography, makeStyles } from '@material-ui/core';
import cn from 'classnames';
import { useEffect, useMemo, useRef } from 'react';
import { PageLoader, Text } from '../../components/common';
import PersonalNoteCard from '../../components/common/PersonalNotes/note-card';
import {
  PersonalNote,
  usePersonalNotes,
} from '../../contexts/personal-notes-context';
import { COLORS } from '../../theme/variables';
import { getFormattedFullDay } from '../../utils/date';

interface NotesListProps {
  hidden?: boolean;
}

const useStyles = makeStyles((theme) => ({
  cardBlock: {
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    height: '100%',
    minHeight: 0,
    maxHeight: '100%',
    overflow: 'hidden',
    alignSelf: 'stretch',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    height: 0,
    width: '100%',
    minHeight: 0,
    padding: '0 0 20px',
    overflow: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',

    '&::-webkit-scrollbar-track': {
      marginTop: '38px',
      borderTop: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    },
  },
  cardGroup: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    width: '100%',
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    minHeight: 0,
    flexShrink: 0,
  },
  emptyMessage: {
    padding: 20,
    textAlign: 'center',
  },
  groupTitle: {
    position: 'sticky',
    top: 0,
    paddingTop: 10,
    paddingLeft: 30,
    paddingBottom: 10,
    margin: 0,
    backgroundColor: '#fff',
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    fontSize: 18,
    lineHeight: '18px',
  },
  loader: {
    marginTop: 20,
    marginBottom: 20,
  },
  hidden: {
    display: 'none',
  },
}));

const groupNotesByMonth = (notes: PersonalNote[]) => {
  const groupedNotes: {
    name: string;
    notes: PersonalNote[];
  }[] = [];

  // Separate target notes from regular notes
  const targetNotes = notes.filter((note) => note.isTargetNote);
  const regularNotes = notes.filter((note) => !note.isTargetNote);

  // Create separate group for target notes
  if (targetNotes.length > 0) {
    groupedNotes.push({ name: 'Featured Note', notes: targetNotes });
  }

  // Sort regular notes by date (newest first)
  const sortedRegularNotes = [...regularNotes].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  // Group regular notes by date
  sortedRegularNotes.forEach((note) => {
    const d = new Date(note.date);
    const date = getFormattedFullDay(d);
    const foundGroup = groupedNotes.find((group) => group.name === date);
    if (foundGroup) {
      foundGroup.notes.push(note);
    } else {
      groupedNotes.push({ name: date, notes: [note] });
    }
  });

  return groupedNotes;
};

function NotesList({ hidden }: NotesListProps) {
  const classes = useStyles();
  const {
    notes,
    selectedNote,
    loading,
    searchFilter,
    onNextPage,
    handleCardSelect,
  } = usePersonalNotes();

  const cardListRef = useRef<HTMLDivElement>(null);

  const groupNotes = useMemo(() => groupNotesByMonth(notes), [notes]);

  // Scroll into view for the selected note on first render after selection
  const hasScrolledRef = useRef<boolean>(false);
  useEffect(() => {
    if (!selectedNote || hasScrolledRef.current) return;
    const selectedId = selectedNote.id;
    if (!selectedId) return;
    // delay to ensure DOM is painted
    const t = window.setTimeout(() => {
      const el = document.getElementById(`note-${selectedId}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
        hasScrolledRef.current = true;
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [selectedNote, groupNotes]);

  useEffect(() => {
    const onScroll = (event: Event) => {
      const target = event.target as HTMLDivElement;
      if (
        target &&
        target.scrollHeight - target.scrollTop <= target.clientHeight + 2000
      ) {
        onNextPage();
      }
    };

    const currentRef = cardListRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', onScroll);

      return () => {
        currentRef.removeEventListener('scroll', onScroll);
      };
    }
  }, [onNextPage, loading]);

  return (
    <div className={cn(classes.cardBlock, { [classes.hidden]: hidden })}>
      <div className={classes.cardList} ref={cardListRef} id='cardList'>
        {groupNotes.map((group, groupIdx) => (
          <div key={groupIdx} className={classes.cardGroup}>
            <Typography variant='h4' className={classes.groupTitle}>
              {group.name}
            </Typography>
            {group.notes.map((note, idx) => (
              <PersonalNoteCard
                key={idx}
                note={note}
                selected={(() => {
                  const noteId = note.id;
                  const selectedId = selectedNote?.id;
                  return !!noteId && !!selectedId && noteId === selectedId;
                })()}
                onClick={() => handleCardSelect(note)}
              />
            ))}
          </div>
        ))}
        {loading && <PageLoader className={classes.loader} />}
        {!loading && !notes.length && (
          <div className={classes.emptyState}>
            <Text variant='normal' className={classes.emptyMessage}>
              {searchFilter
                ? 'We were not able to find any notes with a keyword provided. Please adjust your search prompt and try again.'
                : 'We do not have any notes here yet. Please feel free to create a new one!'}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotesList;
